import { FC } from 'react';
import { range } from 'lodash';

import { icon_arrow_left, icon_arrow_right } from '@gn-audio/icons';
import { Button, ButtonVariantType } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';

import { JabraGallerySliderButton } from 'common/components/JabraGalerySlider';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

export interface JabraSliderNavigationProps {
  activeIndex: number;
  total: number;
  buttonVariant?: ButtonVariantType;
  buttonSize?: 's' | 'm' | 'l';
  buttonClassNames?: string;
  sliderButtonPosition?: 'start' | 'center' | 'end' | 'none';
  goTo?: (index: number) => void;
}

export const PillsSliderNavigation: FC<JabraSliderNavigationProps> = ({
  activeIndex,
  total,
  buttonVariant = 'tertiary',
  buttonSize = 'm',
  buttonClassNames = '',
  sliderButtonPosition = 'start',
  goTo,
}) => {
  const { t } = useFallbackTranslation();
  const handleMoveToSlide = (index: number) => goTo?.(index);

  const changeSlide = (offset: number) => {
    const nextIndex = activeIndex + offset;
    if (nextIndex < 0 || nextIndex >= total) return;
    goTo?.(nextIndex);
  };

  const renderArrowButton = (
    direction: 'left' | 'right',
    onClick: () => void,
    disabled: boolean
  ) => (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      className={buttonClassNames}
      disabled={disabled}
      icon={<JabraIcon Icon={direction === 'left' ? icon_arrow_left : icon_arrow_right} />}
      onClick={onClick}
      aria-label={direction === 'left' ? t('system.Previous') : t('system.Next')}
    />
  );

  const renderSliderButtons = () => (
    <ul className="flex h-[38px] items-center justify-center gap-2">
      {range(total).map((index) => (
        <JabraGallerySliderButton
          key={`button-${index}`}
          onClick={() => handleMoveToSlide(index)}
          active={activeIndex === index}
          index={index}
        />
      ))}
    </ul>
  );

  return (
    <div className="flex items-center gap-3">
      {sliderButtonPosition === 'start' && renderSliderButtons()}
      {renderArrowButton('left', () => changeSlide(-1), activeIndex === 0)}
      {sliderButtonPosition === 'center' && renderSliderButtons()}
      {renderArrowButton('right', () => changeSlide(1), activeIndex >= total - 1)}
      {sliderButtonPosition === 'end' && renderSliderButtons()}
    </div>
  );
};
